import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { useTranslation } from "react-i18next";

import myConfig from "../../configs/config";
import { getMapOptions } from "../PendingSchedules/rumoPistaUtils";
import {
  DEFAULT_RAIN_MAP_ZOOM,
  flattenBoundaryRings,
  formatDateInputBr,
  formatRainMm,
  isUsableRainViewport,
  isValidMapCenter,
  parseLandPlotRing,
  RAIN_MAP_FALLBACK_CENTER,
  clampRainMapZoom,
  rainGaugeMapLabel,
  rainMapScaleMeters,
  shouldShowRainGaugeFullName,
} from "../../utils/rainHeatmapUtils";
import {
  clearRainIdwOverlay,
  createRainIdwOverlay,
} from "../../utils/rainHeatmapOverlay";
import {
  computeRingCentroid,
  LAND_PLOT_MAP_STYLE,
} from "../../utils/mapMeasurements";
import {
  applyViewportToGoogleMap,
} from "../../utils/mapViewport";
import { applyGradualMapZoom } from "../../utils/gradualMapZoom";

function validAreaPoints(ring, gauges) {
  const ringPoints = (Array.isArray(ring) ? ring : []).filter(
    (point) => isValidMapCenter(point)
  );
  if (ringPoints.length >= 3) return ringPoints;
  return (gauges || []).filter((gauge) => isValidMapCenter(gauge));
}

function clampMapZoom(map) {
  if (!map || !map.getZoom) return;
  const zoom = map.getZoom();
  const next = clampRainMapZoom(zoom);
  if (next !== zoom) {
    map.setZoom(next);
  }
}

function areaSpanIsContinental(area) {
  const lats = area.map((point) => Number(point.lat));
  const lngs = area.map((point) => Number(point.lng));
  return (
    Math.max(...lats) - Math.min(...lats) > 2 ||
    Math.max(...lngs) - Math.min(...lngs) > 2
  );
}

function fitMapToRainArea(map, maps, ring, gauges, padding = 48) {
  if (!map || !maps) return false;

  const area = validAreaPoints(ring, gauges);
  if (!area.length) return false;

  if (area.length === 1 || areaSpanIsContinental(area)) {
    const lat =
      area.reduce((sum, point) => sum + Number(point.lat), 0) / area.length;
    const lng =
      area.reduce((sum, point) => sum + Number(point.lng), 0) / area.length;
    if (!isValidMapCenter({ lat, lng })) return false;
    map.setCenter({ lat, lng });
    map.setZoom(DEFAULT_RAIN_MAP_ZOOM);
    return true;
  }

  const bounds = new maps.LatLngBounds();
  area.forEach((point) => {
    bounds.extend(new maps.LatLng(point.lat, point.lng));
  });
  map.fitBounds(bounds, padding);
  if (maps.event) {
    maps.event.addListenerOnce(map, "idle", () => {
      clampMapZoom(map);
    });
  } else {
    clampMapZoom(map);
  }
  return true;
}

function refreshMapSize(map, maps) {
  if (!map || !maps) return;
  if (maps.event) {
    maps.event.trigger(map, "resize");
  }
}

function RainGaugeMarker({
  label,
  compactLabel,
  showFullName,
  mm,
  lastRainDate,
  onSelect,
  selected,
}) {
  const { t } = useTranslation();
  const visibleLabel = showFullName ? label : compactLabel || label;
  const className = [
    "farm-rain-gauge-marker",
    "farm-rain-gauge-marker--button",
    selected ? "farm-rain-gauge-marker--selected" : "",
    showFullName ? "" : "farm-rain-gauge-marker--compact",
  ]
    .filter(Boolean)
    .join(" ");
  const lastRainLabel = lastRainDate
    ? t("Última chuva: {{date}}", { date: formatDateInputBr(lastRainDate) })
    : t("Sem chuva registrada");

  return (
    <button
      type="button"
      className={className}
      title={`${label}: ${formatRainMm(mm)} mm · ${lastRainLabel}`}
      onClick={(event) => {
        event.stopPropagation();
        if (onSelect) onSelect();
      }}
    >
      <span className="farm-rain-gauge-marker__icon">☔</span>
      {visibleLabel ? (
        <span className="farm-rain-gauge-marker__label">{visibleLabel}</span>
      ) : null}
      <strong className="farm-rain-gauge-marker__mm">{formatRainMm(mm)} mm</strong>
      {selected ? (
        <span className="farm-rain-gauge-marker__last-rain">{lastRainLabel}</span>
      ) : null}
    </button>
  );
}

function DraftGaugePin() {
  return <div className="farm-rain-draft-pin" aria-hidden />;
}

function FarmRainHeatmapMap({
  boundaryRing,
  subFarms = [],
  landPlots,
  gauges,
  center,
  zoom,
  savedViewport,
  mapHeight = 560,
  selectedGaugeId,
  selectedSubFarmId = "",
  focusRing = null,
  focusToken = 0,
  onGaugeClick,
  onMapReady,
  onViewportIdle,
  onSubFarmClick,
  placingMode = false,
  draftPoint = null,
  onMapClick,
}) {
  const [showFullGaugeName, setShowFullGaugeName] = useState(false);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const heatmapRef = useRef(null);
  const farmOverlaysRef = useRef([]);
  const plotPolygonsRef = useRef([]);
  const appliedViewRef = useRef(false);
  const gaugesRef = useRef(gauges);
  gaugesRef.current = gauges;
  const onViewportIdleRef = useRef(onViewportIdle);
  onViewportIdleRef.current = onViewportIdle;
  const focusTokenRef = useRef(focusToken);
  focusTokenRef.current = focusToken;
  const focusRingRef = useRef(focusRing);
  focusRingRef.current = focusRing;

  const updateGaugeLabelMode = useCallback((map) => {
    if (!map || !map.getZoom || !map.getCenter) return;
    const mapCenter = map.getCenter();
    if (!mapCenter) return;
    const scaleMeters = rainMapScaleMeters({
      zoom: map.getZoom(),
      latitude: mapCenter.lat(),
    });
    const next = shouldShowRainGaugeFullName(scaleMeters);
    setShowFullGaugeName((current) => (current === next ? current : next));
  }, []);

  const emitViewport = useCallback((map) => {
    const callback = onViewportIdleRef.current;
    if (!callback || !map || !map.getBounds || !map.getCenter) return;
    const bounds = map.getBounds();
    const mapCenter = map.getCenter();
    if (!bounds || !mapCenter) return;
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    if (!northEast || !southWest) return;
    callback({
      center: { lat: mapCenter.lat(), lng: mapCenter.lng() },
      south: southWest.lat(),
      west: southWest.lng(),
      north: northEast.lat(),
      east: northEast.lng(),
    });
  }, []);

  const applyFocusRing = useCallback((map, maps) => {
    const ring = focusRingRef.current;
    if (!map || !maps || !Array.isArray(ring) || ring.length < 3) return false;
    return fitMapToRainArea(map, maps, ring, [], 56);
  }, []);

  const heatmapRings = useMemo(() => {
    const subRings = (subFarms || [])
      .map((farm) => farm.ring)
      .filter((ring) => Array.isArray(ring) && ring.length >= 3);
    if (subRings.length) return subRings;
    if (Array.isArray(boundaryRing) && boundaryRing.length >= 3) {
      return [boundaryRing];
    }
    return [];
  }, [subFarms, boundaryRing]);

  const fitRing = useMemo(
    () => flattenBoundaryRings(heatmapRings),
    [heatmapRings]
  );

  const maxGaugeRainMm = useMemo(() => {
    const values = (gauges || []).map((gauge) => Number(gauge.mm) || 0);
    if (!values.length) return 0;
    return Math.max(...values, 0);
  }, [gauges]);

  const colorScaleMaxMm = useMemo(
    () => Math.max(maxGaugeRainMm, 1),
    [maxGaugeRainMm]
  );

  const clearFarmOverlays = useCallback(() => {
    farmOverlaysRef.current.forEach((overlay) => {
      if (overlay && overlay.setMap) overlay.setMap(null);
    });
    farmOverlaysRef.current = [];
  }, []);

  const clearPlotPolygons = useCallback(() => {
    plotPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    plotPolygonsRef.current = [];
  }, []);

  const renderStaticPolygons = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    clearFarmOverlays();
    clearPlotPolygons();

    const handlePolygonClick = (event) => {
      if (!placingMode || !onMapClick || !event || !event.latLng) return;
      onMapClick({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    };

    const farmsToDraw = (subFarms || []).filter(
      (farm) => Array.isArray(farm.ring) && farm.ring.length >= 3
    );
    const ringsToDraw = farmsToDraw.length
      ? farmsToDraw.map((farm) => ({
          id: farm.id,
          ring: farm.ring,
          name: farm.name,
        }))
      : Array.isArray(boundaryRing) && boundaryRing.length >= 3
      ? [{ ring: boundaryRing, name: "" }]
      : [];

    ringsToDraw.forEach((farm) => {
      const selected =
        selectedSubFarmId && String(farm.id) === String(selectedSubFarmId);
      const polygon = new maps.Polygon({
        paths: farm.ring,
        map,
        strokeColor: selected ? "#0D47A1" : "#1565C0",
        strokeOpacity: selected ? 1 : 0.7,
        strokeWeight: selected ? 4 : 2.5,
        fillColor: "#1565C0",
        fillOpacity: selected ? 0.08 : 0,
        zIndex: selected ? 4 : 2,
        clickable: placingMode || Boolean(onSubFarmClick && farm.id),
      });
      if (placingMode) {
        polygon.addListener("click", handlePolygonClick);
      } else if (onSubFarmClick && farm.id) {
        polygon.addListener("click", () => onSubFarmClick(farm.id));
      }
      farmOverlaysRef.current.push(polygon);

      const centroid = computeRingCentroid(farm.ring);
      if (centroid && farm.name) {
        farmOverlaysRef.current.push(
          new maps.Marker({
            map,
            position: centroid,
            label: {
              text: farm.name,
              color: selected ? "#0D47A1" : "#1565C0",
              fontWeight: "bold",
              fontSize: selected ? "13px" : "12px",
            },
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 0,
              fillOpacity: 0,
              strokeOpacity: 0,
            },
            zIndex: 8,
            clickable: false,
          })
        );
      }
    });

    (landPlots || []).forEach((plot) => {
      const ring = parseLandPlotRing(plot);
      if (ring.length < 3) return;
      const polygon = new maps.Polygon({
        paths: ring,
        map,
        strokeColor: LAND_PLOT_MAP_STYLE.strokeColor,
        strokeOpacity: 0.7,
        strokeWeight: 1.5,
        fillColor: LAND_PLOT_MAP_STYLE.fillColor,
        fillOpacity: 0.04,
        zIndex: 3,
        clickable: placingMode,
      });
      if (placingMode) {
        polygon.addListener("click", handlePolygonClick);
      }
      plotPolygonsRef.current.push(polygon);
    });
  }, [
    boundaryRing,
    subFarms,
    landPlots,
    clearFarmOverlays,
    clearPlotPolygons,
    placingMode,
    onMapClick,
    selectedSubFarmId,
    onSubFarmClick,
  ]);

  const renderHeatmap = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    if (!heatmapRef.current) {
      heatmapRef.current = createRainIdwOverlay(maps);
      heatmapRef.current.setMap(map);
    }

    heatmapRef.current.setData({
      rings: heatmapRings,
      gauges,
      maxMm: colorScaleMaxMm,
    });
  }, [heatmapRings, gauges, colorScaleMaxMm]);

  const updateHeatmap = useCallback(() => {
    renderHeatmap();
  }, [renderHeatmap]);

  const scheduleHeatmapRender = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps || !maps.event) {
      window.setTimeout(() => renderHeatmap(), 200);
      return;
    }

    maps.event.addListenerOnce(map, "idle", () => {
      renderHeatmap();
    });
  }, [renderHeatmap]);

  useEffect(() => {
    updateHeatmap();
  }, [updateHeatmap]);

  useEffect(() => {
    renderStaticPolygons();
  }, [renderStaticPolygons]);

  useEffect(
    () => () => {
      if (heatmapRef.current) {
        clearRainIdwOverlay(heatmapRef.current);
        heatmapRef.current = null;
      }
      clearFarmOverlays();
      clearPlotPolygons();
    },
    [clearFarmOverlays, clearPlotPolygons]
  );

  const applyOpeningView = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    const node = containerRef.current;
    if (!map || !maps) return;
    if (!node || node.offsetWidth < 40 || node.offsetHeight < 40) return;

    if (isUsableRainViewport(savedViewport)) {
      applyViewportToGoogleMap(map, {
        ...savedViewport,
        zoom: clampRainMapZoom(savedViewport.zoom),
      });
      appliedViewRef.current = true;
      return;
    }

    if (appliedViewRef.current) return;
    if (fitMapToRainArea(map, maps, fitRing, gaugesRef.current)) {
      appliedViewRef.current = true;
    }
  }, [fitRing, savedViewport]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;
    refreshMapSize(map, maps);
    scheduleHeatmapRender();
  }, [fitRing, gauges, scheduleHeatmapRender]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;
    refreshMapSize(map, maps);
    applyOpeningView();
    scheduleHeatmapRender();
  }, [mapHeight, applyOpeningView, scheduleHeatmapRender]);

  useEffect(() => {
    if (!focusToken) return;
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;
    applyFocusRing(map, maps);
  }, [focusToken, applyFocusRing]);

  const handleGoogleApiLoaded = ({ map, maps }) => {
    if (heatmapRef.current) {
      clearRainIdwOverlay(heatmapRef.current);
      heatmapRef.current = null;
    }
    mapRef.current = map;
    mapsRef.current = maps;
    applyGradualMapZoom(map);
    appliedViewRef.current = false;
    map.setMapTypeId(maps.MapTypeId.SATELLITE);
    refreshMapSize(map, maps);
    applyOpeningView();
    renderStaticPolygons();
    scheduleHeatmapRender();
    if (onMapReady) onMapReady(map);

    window.requestAnimationFrame(() => {
      refreshMapSize(map, maps);
      applyOpeningView();
      renderStaticPolygons();
      scheduleHeatmapRender();
    });

    if (maps.event) {
      maps.event.addListenerOnce(map, "idle", () => {
        refreshMapSize(map, maps);
        map.setMapTypeId(maps.MapTypeId.SATELLITE);
        applyOpeningView();
        clampMapZoom(map);
        if (focusTokenRef.current) {
          applyFocusRing(map, maps);
        }
      });
      maps.event.addListener(map, "idle", () => {
        emitViewport(map);
        updateGaugeLabelMode(map);
      });
      maps.event.addListener(map, "zoom_changed", () => {
        updateGaugeLabelMode(map);
      });
      updateGaugeLabelMode(map);
    }
  };

  const mapCenter = isValidMapCenter(center)
    ? center
    : RAIN_MAP_FALLBACK_CENTER;

  return (
    <div
      className={
        placingMode
          ? "farm-rain-heatmap-map farm-rain-heatmap-map--placing"
          : "farm-rain-heatmap-map"
      }
      ref={containerRef}
      style={{ height: mapHeight }}
    >
      <div className="farm-rain-heatmap-map__surface">
        <GoogleMapReact
          key="rain-heatmap-satellite"
          bootstrapURLKeys={{
            key: myConfig.GOOGLE_API_KEY,
            libraries: ["geometry"],
          }}
          defaultCenter={mapCenter}
          defaultZoom={clampRainMapZoom(zoom)}
          options={(maps) => ({
            ...getMapOptions(maps),
            draggableCursor: placingMode ? "crosshair" : undefined,
          })}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={handleGoogleApiLoaded}
          onClick={({ lat, lng }) => {
            if (onMapClick) onMapClick({ lat, lng });
          }}
        >
          {(gauges || [])
            .filter((gauge) => isValidMapCenter(gauge))
            .map((gauge) => (
              <RainGaugeMarker
                key={gauge.id}
                lat={gauge.lat}
                lng={gauge.lng}
                label={gauge.label}
                compactLabel={rainGaugeMapLabel(gauge, false)}
                showFullName={showFullGaugeName}
                mm={gauge.mm}
                lastRainDate={gauge.lastRainDate}
                selected={selectedGaugeId === gauge.id}
                onSelect={
                  onGaugeClick ? () => onGaugeClick(gauge) : undefined
                }
              />
            ))}
          {draftPoint && isValidMapCenter(draftPoint) ? (
            <DraftGaugePin lat={draftPoint.lat} lng={draftPoint.lng} />
          ) : null}
        </GoogleMapReact>
      </div>
      <div
        className="farm-rain-heatmap-legend"
        aria-label={`Legenda de chuva de 0 mm a ${formatRainMm(maxGaugeRainMm)} mm`}
      >
        <span>0 mm</span>
        <div className="farm-rain-heatmap-legend__bar" />
        <span>{formatRainMm(maxGaugeRainMm)} mm</span>
      </div>
    </div>
  );
}

export default FarmRainHeatmapMap;
